import BiwengerIcon from '@/components/shared/icons/biwenger-icon';
import FantasyLaLigaIcon from '@/components/shared/icons/fantasy-la-liga-icon';
import CustomModal from '@/DesignSystem/components/custom-modal/CustomModal';
import { Box, Typography, Divider, Button } from '@mui/material';
import { getPriceColor, toPriceInEuros } from '@/helpers/prices-helper';
import { Rutas } from '@/constants/rutas';
import Link from 'next/link';
import PlacementModalComponent from '@/components/ads-components/placements/placementModal';
import FantasyMarcaIcon from '@/components/shared/icons/FantasyMarcaIcon';
import useStatusIcon from '@/DesignSystem/hooks/useStatusIcon';
import InfoIcon from '@/components/shared/icons/info-icon';
import TipsIcon from '@/components/shared/icons/tips-icon';
import MisterFantasyIcon from '@/components/shared/icons/MisterFantasyIcon';
import Porcentajes from '@/components/alineaciones/shared/complementos/Porcentajes';
import useShowPlayerAlineacionProbable from '@/components/alineaciones/shared/hooks/useShowPlayerAlineacionProbable';
import BufonIcon from '../icons/recommendation-icons/bufon-icon';
import ArieteIcon from '../icons/recommendation-icons/ariete-icon';
import ChollosIcon from '../icons/recommendation-icons/chollos-icon';
import RevulsivoIcon from '../icons/recommendation-icons/revulsivo-icon';
import CapitanArieteIcon from '../icons/recommendation-icons/capitan-ariete-icon';
import CapitanIcon from '../icons/recommendation-icons/capitan-icon';
import { useContext } from 'react';
import { Context } from '@/context';
import { REDUCERS } from '@/constants/reducers';
import { currentSeason } from '@/constants/site';
import { useGlobalRankings } from '@/hooks/useGlobalRankings';
import { usePlayerRankings } from '@/hooks/usePlayerRankings';
import FantasyIconSelector from '@/components/shared/icons/FantasyIconSelector';
import { FANTASY } from '@/helpers/biwenger-helper';

const donquiPickIcons = {
    bufon: {
        icon: <BufonIcon width={16} height={16} />,
        title: 'Bufón',
        description: 'Probabilidades de mala jornada, por rotación, suplencia o bajo rendimiento.',
    },
    ariete: {
        icon: <ArieteIcon width={16} height={16} />,
        title: 'Ariete',
        description: 'Probabilidades de ver portería.',
    },
    chollo: {
        icon: <ChollosIcon width={16} height={16} />,
        title: 'Chollo',
        description: 'Probabilidades de buena jornada a buen precio.',
    },
    revulsivo: {
        icon: <RevulsivoIcon width={16} height={16} />,
        title: 'Revulsivo',
        description: 'Probabilidades de buena jornada partiendo desde el banquillo.',
    },
    capitan: {
        icon: <CapitanIcon width={16} height={16} />,
        title: 'Capitán',
        description: 'Probabilidades de buena jornada.',
    },
    capitanAriete: {
        icon: <CapitanArieteIcon width={16} height={16} />,
        title: 'Capitán Ariete',
        description: 'Probabilidades de buena jornada y de marcar.',
    },
};

const JugadorLineupInformationModalActions = ({ player, onClose }) => {
    const { dispatch } = useContext(Context);
    const playerId = player.id;

    const handleClickOnPlayerProfile = () => {
        onClose();
        dispatch({
            type: REDUCERS.SHOW_PLAYER_PROFILE,
            payload: { playerId, selectedIndex: 2 },
        });
    };

    const handleClickOnPlayerHistoric = () => {
        onClose();
        dispatch({
            type: REDUCERS.SHOW_FANTASY_HISTORIC,
            payload: { playerId, season: currentSeason },
        });
    };

    const handleClickOnPlayerProjection = () => {
        onClose();
        dispatch({
            type: REDUCERS.SHOW_MARKET_PROJECTION,
            payload: playerId,
        });
    };

    const handleClickOnPlayerCalendar = () => {
        onClose();
        dispatch({
            type: REDUCERS.SHOW_PLAYER_CALENDAR,
            payload: playerId,
        });
    };

    const handleClickOnPlayerMinutes = () => {
        onClose();
        dispatch({
            type: REDUCERS.SHOW_PLAYER_MINUTES,
            payload: playerId,
        });
    };

    const menus = [
        { label: 'Perfil', onClick: handleClickOnPlayerProfile },
        { label: 'Mercado', onClick: handleClickOnPlayerHistoric },
        { label: 'Proyección', onClick: handleClickOnPlayerProjection },
        { label: 'Calendario', onClick: handleClickOnPlayerCalendar },
        { label: 'Minutos', onClick: handleClickOnPlayerMinutes },
    ];

    return (
        <Box display="flex" gap={0.5} justifyContent="center" width={'100%'}>
            {menus.map((menu) => (
                <Button
                    color="primary"
                    key={menu.label}
                    href={menu.ruta}
                    variant="text"
                    sx={{
                        fontSize: '14px',
                        p: 0,
                        margin: 0,
                    }}
                    onClick={menu.onClick}
                >
                    {menu.label}
                </Button>
            ))}
        </Box>
    );
};

const JugadorLineupInformationModal = ({ player, open }) => {
    const statusIcon = useStatusIcon({
        status: player?.status,
        statusLevel: player?.statusLevel,
        width: 14,
        height: 14,
        skipIfNotOk: false,
        includeLabel: true,
    });

    const handleClose = useShowPlayerAlineacionProbable(null);

    // Obtener rankings del jugador
    const playerRanking = usePlayerRankings(player?.id);
    const { rankings } = useGlobalRankings();
    const allPlayerRankings = player?.id ? rankings[player.id] : null;

    const getText = () => {
        if (player.chance >= 90) {
            return 'Titularidad prácticamente asegurada, salvo sorpresa o noticia de última hora ';
        } else if (player.chance >= 70) {
            return 'Titularidad muy probable, creemos que juega';
        } else if (player.chance > 50) {
            return 'Titularidad probable, pero tiene riesgo de rotación o suplencia';
        } else if (player.chance >= 40) {
            return 'Titularidad poco probable, pero tiene posibilidades de jugar ya sea titular o suplente';
        } else {
            return 'Titularidad muy poco probable o nula, mejor buscar otras opciones';
        }
    };

    const subidaStyles = {
        fontSize: '12px',
        color: '#fff',
        padding: '2px 8px',
        borderRadius: '4px',
    };

    const getDonquiPickIcon = () => {
        if (player.isBufon || player.isAriete || player.isChollo || player.isRevulsivo || player.isCapitan) {
            let donquiPick = {};
            if (player.isBufon) {
                donquiPick = donquiPickIcons.bufon;
            } else if (player.isAriete) {
                donquiPick = donquiPickIcons.ariete;
            } else if (player.isChollo) {
                donquiPick = donquiPickIcons.chollo;
            } else if (player.isRevulsivo) {
                donquiPick = donquiPickIcons.revulsivo;
            } else if (player.isCapitan) {
                donquiPick = donquiPickIcons.capitan;
            }
            return (
                <>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '14px', mb: 1 }}>
                            Donqui Pick: {donquiPick.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {donquiPick.icon}
                            <Typography sx={{ fontSize: '14px' }}>{donquiPick.description}</Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 1, backgroundColor: 'border.light' }} />
                </>
            );
        }
        return null;
    };

    return (
        <CustomModal
            open={open}
            onClose={handleClose}
            title={player.name}
            actions={<JugadorLineupInformationModalActions player={player} onClose={handleClose} />}
            contentSx={{
                maxHeight: '70vh',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '4px',
                    '&:hover': {
                        background: '#a8a8a8',
                    },
                },
            }}
            playerId={player.id}
        >
            {/* Comentario */}
            {player.comentario && (
                <>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '14px', mb: 1 }}>
                            Comentario
                        </Typography>
                        <Typography sx={{ fontSize: '14px' }}>{player.comentario}</Typography>
                    </Box>
                    <Divider sx={{ mb: 1, backgroundColor: 'border.light' }} />
                </>
            )}

            {/* Probabilidad de titular */}
            <Box sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontSize: '14px', mb: 1 }}>
                    Probabilidad de titular
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Porcentajes porcentaje={player.chance} height={16} width={'30px'} fontSize={10} />
                    <Typography sx={{ fontSize: '14px' }}>{getText()}</Typography>
                </Box>
            </Box>

            <Divider sx={{ mb: 1, backgroundColor: 'border.light' }} />

            {/* Ranking Global */}
            {playerRanking && allPlayerRankings && (
                <>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '14px', mb: 1 }}>
                            Ranking Global
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box
                                sx={{
                                    width: 24,
                                    height: 37,
                                    borderRadius: '4px',
                                    backgroundImage: `url(${process.env.NEXT_PUBLIC_IMAGES}/cards/${playerRanking.tier}.png?width=36&height=56)`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    flexShrink: 0,
                                }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                                    {playerRanking.tier === 'red'
                                        ? 'Top 10 en Bajadas de Mercado'
                                        : `Top ${
                                              playerRanking.tier === 'gold'
                                                  ? '1'
                                                  : playerRanking.tier === 'silver'
                                                    ? '5'
                                                    : '10'
                                          } en ${
                                              playerRanking.category === 'mercado'
                                                  ? playerRanking.marketType === 'up'
                                                      ? 'Subidas de Mercado'
                                                      : playerRanking.marketType === 'value'
                                                        ? 'Valor de Mercado'
                                                        : 'Mercado'
                                                  : playerRanking.category === 'fantasy'
                                                    ? 'La Liga Fantasy'
                                                    : playerRanking.category === 'mister'
                                                      ? 'Mister Fantasy'
                                                      : playerRanking.category === 'marca'
                                                        ? 'Fantasy Marca'
                                                        : 'Biwenger'
                                          }`}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                                    Este jugador está en el top global de alguna de las categorías, por eso
                                    aparece con una tarjeta especial en la alineación.
                                </Typography>
                            </Box>
                        </Box>

                        {/* Mostrar todos los rankings del jugador */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                            {allPlayerRankings.laLigaFantasy && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FantasyIconSelector fantasy={FANTASY.FANTASY} size={16} />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        La Liga Fantasy:{' '}
                                        <strong>TOP {allPlayerRankings.laLigaFantasy}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.fantasyMarca && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FantasyIconSelector fantasy={FANTASY.FANTASY_MARCA} size={16} />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Fantasy Marca: <strong>TOP {allPlayerRankings.fantasyMarca}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.misterFantasy && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FantasyIconSelector fantasy={FANTASY.MISTER_FANTASY} size={16} />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Mister Fantasy: <strong>TOP {allPlayerRankings.misterFantasy}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.biwenger && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FantasyIconSelector fantasy={FANTASY.BIWENGER} size={16} />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Biwenger: <strong>TOP {allPlayerRankings.biwenger}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.marketValueUp && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '13px' }}>📈</Typography>
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Subidas de Mercado:{' '}
                                        <strong>TOP {allPlayerRankings.marketValueUp}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.marketValue && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '13px' }}>💰</Typography>
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Valor de Mercado: <strong>TOP {allPlayerRankings.marketValue}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.marketValueDown && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '13px' }}>📉</Typography>
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Bajadas de Mercado:{' '}
                                        <strong>TOP {allPlayerRankings.marketValueDown}</strong>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Divider sx={{ mb: 1, backgroundColor: 'border.light' }} />
                </>
            )}

            {/* Probabilidad de titular */}
            {getDonquiPickIcon()}

            {/* Estado del jugador */}
            <Box sx={{ mb: 1 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontSize: '14px', mb: 1 }}>
                        Estado del jugador
                    </Typography>
                    <Typography sx={{ fontSize: '14px' }}>{statusIcon}</Typography>
                </Box>

                {player.report && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InfoIcon iconWidth={14} />
                        <Typography sx={{ fontSize: '14px' }}>{player.report}</Typography>
                    </Box>
                )}
                {player.statusInfo && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TipsIcon iconWidth={14} />
                        <Typography sx={{ fontSize: '14px' }}>{player.statusInfo}</Typography>
                    </Box>
                )}
            </Box>

            <Divider sx={{ mb: 1, backgroundColor: 'border.light' }} />

            <PlacementModalComponent />

            {/* La Liga Fantasy */}
            <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <FantasyLaLigaIcon iconWidth={16} iconHeight={16} />
                    <Typography variant="h6" sx={{ fontSize: '14px' }}>
                        La Liga Fantasy
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px' }}>Valor de mercado:</Typography>
                        <Typography sx={{ fontSize: '14px' }}>
                            {toPriceInEuros(player.laLigaFantasyMarketValue)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px' }}>Subida:</Typography>
                        <Typography
                            sx={{
                                ...subidaStyles,
                                background: getPriceColor(player.laLigaFantasySubida),
                            }}
                        >
                            {toPriceInEuros(player.laLigaFantasySubida)}
                        </Typography>
                    </Box>
                    {player.laLigaFantasyPoints > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '14px' }}>Puntos:</Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {player.laLigaFantasyPoints || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                    {player.laLigaFantasyAverage > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '14px' }}>Promedio:</Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {player.laLigaFantasyAverage?.toFixed(2) || '-'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Divider sx={{ mb: 1, backgroundColor: 'border.light' }} />

            {/* Biwenger */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <BiwengerIcon width={16} height={16} />
                    <Typography variant="h6" sx={{ fontSize: '14px' }}>
                        Biwenger
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px' }}>Valor de mercado:</Typography>
                        <Typography sx={{ fontSize: '14px' }}>
                            {toPriceInEuros(player.biwengerMarketValue)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px' }}>Subida:</Typography>
                        <Typography
                            sx={{
                                ...subidaStyles,
                                background: getPriceColor(player.biwengerSubida),
                            }}
                        >
                            {toPriceInEuros(player.biwengerSubida)}
                        </Typography>
                    </Box>
                    {player.biwengerAsPoints > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '14px' }}>Puntos AS:</Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {player.biwengerAsPoints || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                    {player.biwengerSofascorePoints > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '14px' }}>Puntos Sofascore:</Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {player.biwengerSofascorePoints || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                    {player.biwengerStatsPoints > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '14px' }}>Puntos Stats:</Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {player.biwengerStatsPoints || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                    {player.biwengerAveragePoints > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '14px' }}>Promedio de puntos:</Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {player.biwengerAveragePoints || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                    {player.biwengerSocialPoints > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '14px' }}>Puntos sociales:</Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {player.biwengerSocialPoints || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Divider sx={{ mb: 1, backgroundColor: 'border.light', mt: 1 }} />

            {/* Fantasy Marca */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <FantasyMarcaIcon width={16} height={16} />
                    <Typography variant="h6" sx={{ fontSize: '14px' }}>
                        Fantasy Marca
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px' }}>Valor de mercado:</Typography>
                        <Typography sx={{ fontSize: '14px' }}>
                            {toPriceInEuros(player.fantasyMarcaMarketValue)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px' }}>Subida:</Typography>
                        <Typography
                            sx={{
                                ...subidaStyles,
                                background: getPriceColor(player.fantasyMarcaSubida),
                            }}
                        >
                            {toPriceInEuros(player.fantasyMarcaSubida)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ mb: 1, backgroundColor: 'border.light', mt: 1 }} />

            {/* Mister Fantasy */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <MisterFantasyIcon width={16} height={16} />
                    <Typography variant="h6" sx={{ fontSize: '14px' }}>
                        Mister Fantasy
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px' }}>Valor de mercado:</Typography>
                        <Typography sx={{ fontSize: '14px' }}>
                            {toPriceInEuros(player.misterFantasyMarketValue)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px' }}>Subida:</Typography>
                        <Typography
                            sx={{
                                ...subidaStyles,
                                background: getPriceColor(player.misterFantasySubida),
                            }}
                        >
                            {toPriceInEuros(player.misterFantasySubida)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </CustomModal>
    );
};

export default JugadorLineupInformationModal;
